import { z } from 'zod';
import config from '@packages/config/src';
import {
  fieldMessageGeneral,
  fieldMessagesInvalid,
  fieldMessagesLength,
  fieldMessagesRequired,
} from './AddressValidation.messages';

export const FieldErrorReason = {
  required: 'Required', // Required (with big R) to avoid confusion with zod's required_error
  invalid: 'Invalid',
  streetPP: 'StreetPackstationPostfiliale',
  length: 'Length',
};

export const getFieldError = (reason: string, fieldName: string) => {
  if (reason === FieldErrorReason.required) {
    return fieldMessagesRequired[fieldName as keyof typeof fieldMessagesRequired];
  }
  if (reason === FieldErrorReason.invalid) {
    return fieldMessagesInvalid[fieldName as keyof typeof fieldMessagesInvalid];
  }
  if (reason === FieldErrorReason.length) {
    return fieldMessagesLength.name;
  }
  if (reason === FieldErrorReason.streetPP) {
    return fieldMessagesInvalid.streetPackOrPost;
  }
  return fieldMessageGeneral.somethingWrong;
};

const nameRegex = /^[A-Za-zÄÖÜäöü][A-Za-z'.\-\sßÄÖÜäöü]*$/;

export const phoneNumberCharacterPattern = /^[0-9()+\-/ ]*$/;

// First and last name are limited to 2-30 characters in the inline validation (IOGZ-423).
export const NAME_MIN_LENGTH = 2;
export const NAME_MAX_LENGTH = 30;

/**
 * Street and city share one rule, taken verbatim from the legacy checkout
 * (fragment-order-checkout/src/components/RegisterForm/registrationRules.tsx) so both checkouts
 * accept the same input: Latin-1 accents plus Ć-ĉ, up to 40 characters.
 *
 * Known limitation inherited from there: Latin letters outside Latin-1 are missing, so a Polish
 * "Świętokrzyska" fails. That only bites for an address in the tenant's own country now, because
 * addresses abroad skip these checks entirely (see below).
 */
const addressTextRegex = /^[A-Za-z0-9'´À-ÖØ-öø-ÿĆ-ĉ/., -]{0,40}$/u;
const parcelShopRegex = /^(?!.*(packstation|postfiliale)).*$/i;
const houseNumberRegex = /^[1-9]([a-z0-9.,;/ +-]*[a-z0-9])?\s*$/;
const zipCodeRegex = new RegExp(`^[0-9]{${config.order.addressValidation.zipCode.length}}$`);

/** Country the shop itself is operated in, derived from the tenant's default locale ('de-DE' -> 'DE'). */
export const tenantCountryCode = config.i18n.defaultLocale.split('-')[1] ?? '';

/**
 * An empty country counts as domestic: the country field has its own required check and
 * until it is answered the tenant's own country is the only sensible assumption.
 */
export const isTenantCountry = (countryCode?: string | null) =>
  !countryCode || countryCode.toUpperCase() === tenantCountryCode;

/**
 * Address field that is either mandatory and format checked, or – when the address is not
 * validated at all – a plain string that accepts anything, including an empty value.
 */
const addressField = (validate: boolean, ...formats: Array<[RegExp, string]>) => {
  if (!validate) {
    return z.string();
  }

  return formats.reduce(
    (field, [format, message]) => field.regex(format, { message }),
    z.string().min(1, { message: FieldErrorReason.required }),
  );
};

export type AddressValidationSchemaOptions = {
  /**
   * Validate street, house number, zip code and city at all (default).
   *
   * Turn it off for an address in another country than the tenant's ({@link isTenantCountry}):
   * those fields then accept anything, so customers from abroad can enter their real address
   * whatever its local shape. This mirrors the legacy checkout, which drops the required, format
   * and Packstation checks for a foreign country alike and leaves the address to the backend
   * (fragment-order-checkout/src/components/RegisterForm/RegistrationFormContext.tsx).
   *
   * Everything that is not part of the address – gender, names, phone number – stays validated.
   */
  validateAddressFields?: boolean;
};

export const addressValidationSchema = (
  markFieldsMandatory: boolean,
  { validateAddressFields = true }: AddressValidationSchemaOptions = {},
) =>
  z.object({
    gender: markFieldsMandatory
      ? z
          .string({ required_error: FieldErrorReason.required })
          .min(1, { message: FieldErrorReason.required })
      : z.string().optional(),
    firstName: markFieldsMandatory
      ? z
          .string()
          .min(1, { message: FieldErrorReason.required })
          .min(NAME_MIN_LENGTH, { message: FieldErrorReason.length })
          .max(NAME_MAX_LENGTH, { message: FieldErrorReason.length })
          .regex(nameRegex, { message: FieldErrorReason.invalid })
      : z.string().optional(),
    lastName: markFieldsMandatory
      ? z
          .string()
          .min(1, { message: FieldErrorReason.required })
          .min(NAME_MIN_LENGTH, { message: FieldErrorReason.length })
          .max(NAME_MAX_LENGTH, { message: FieldErrorReason.length })
          .regex(nameRegex, { message: FieldErrorReason.invalid })
      : z.string().optional(),
    street: markFieldsMandatory
      ? addressField(
          validateAddressFields,
          [addressTextRegex, FieldErrorReason.invalid],
          [parcelShopRegex, FieldErrorReason.streetPP],
        )
      : z.string().optional(),
    houseNumber: markFieldsMandatory
      ? addressField(validateAddressFields, [houseNumberRegex, FieldErrorReason.invalid])
      : z.string().optional(),
    additional: z.string().optional(),
    zipCode: markFieldsMandatory
      ? addressField(validateAddressFields, [zipCodeRegex, FieldErrorReason.invalid])
      : z.string().optional(),
    city: markFieldsMandatory
      ? addressField(validateAddressFields, [addressTextRegex, FieldErrorReason.invalid])
      : z.string().optional(),
    primaryPhoneNumber: z
      .string()
      .regex(phoneNumberCharacterPattern, { message: FieldErrorReason.invalid })
      .optional()
      .or(z.literal('')),
  });
